import React from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { getContrastColor } from '#component/theme';
import { isActiveDevice } from '#component/device/helper';
import DevicesTooltip from './devices-tooltip';
import DeviceTooltip from './device-tooltip';

function addPlaceHover() {
  const { map } = this;
  const { mapboxgl } = window;
  const { account, theme, meta } = map.app;
  // Create a popup, but don't add it to the map yet.
  const popup = new mapboxgl.Popup({
    offset: 12,
    closeButton: false,
    closeOnClick: false,
    maxWidth: 320
  });

  const div = document.createElement('div');
  const root = createRoot(div);

  map.on('mouseenter', 'places', e => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';

    // Copy coordinates array.
    const coordinates = e.features[0].geometry.coordinates.slice();
    const device = e.features[0].properties;

    root.render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <DeviceTooltip device={device} account={account} meta={meta} />
        </ThemeProvider>
      </StyledEngineProvider>
    );

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup.setLngLat(coordinates).setDOMContent(div).addTo(map);
  });
  map.on('mouseleave', 'places', () => {
    map.getCanvas().style.cursor = '';
    // prevents jumping on load
    setTimeout(() => root.unmount());
    popup.remove();
  });
}

function addClusterHover() {
  const { map } = this;
  const { mapboxgl } = window;
  const { theme } = map.app;
  // Create a popup, but don't add it to the map yet.
  const popup = new mapboxgl.Popup({
    offset: 12,
    closeButton: false,
    closeOnClick: false,
    maxWidth: 320
  });

  const div = document.createElement('div');
  const root = createRoot(div);

  map.on('mouseenter', 'clusters', e => {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = 'pointer';

    // Copy coordinates array.
    const coordinates = e.features[0].geometry.coordinates.slice();
    const { properties } = e.features[0];

    root.render(
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <DevicesTooltip
            properties={properties}
            meta={map.app.meta}
          />
        </ThemeProvider>
      </StyledEngineProvider>
    );

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }

    // Populate the popup and set its coordinates
    // based on the feature found.
    const { point_count: c } = properties;
    const offset = c >= 750 ? 30 : c >= 100 ? 26 : c >= 10 ? 18 : 12;
    popup.setOffset(offset);
    popup.setLngLat(coordinates).setDOMContent(div).addTo(map);
  });
  map.on('mouseleave', 'clusters', () => {
    map.getCanvas().style.cursor = '';
    // prevents jumping on load
    setTimeout(() => root.unmount());
    popup.remove();
  });
}

function addPlaceClick() {
  const { map } = this;
  map.on('click', 'places', e => {
    const device = e.features[0].properties;
    map.app.helper.setClickedDevices([device]);
  });
}

function addClusterClick() {
  const { map } = this;
  map.on('click', 'clusters', e => {
    const sourceObject = map.getSource('locations');
    sourceObject.getClusterLeaves(
      e.features[0].id,
      e.features[0].properties.point_count,
      0,
      (error, features) => {
        if (!error) {
          map.app.helper.setClickedDevices(features.map(f => f.properties));
        }
      }
    );
  });
}

function addClusterProperties(geojson, meta) {
  const clusterProperties = {};
  meta.device.forEach(m => {
    clusterProperties[m.name] = ['+', ['case', ['==', m.name, ['get', 'type']], 1, 0]];
    clusterProperties.occupier = ['+', ['case', ['==', ['get', 'occupier'], 1], 1, 0]];
  });
  geojson.clusterProperties = clusterProperties;
  return geojson;
}

function getGeoJSON(data, defaultMapCenter) {
  let minLng = null;
  let maxLng = null;
  let minLat = null;
  let maxLat = null;
  const features = [];
  data.forEach(d => {
    const { lng, lat, timestamp } = d.lastLocation || {};
    if (lng ?? lng) {
      if (d.lastLocation?.lng < minLng || !minLng) minLng = lng;
      if (d.lastLocation?.lat < minLat || !minLat) minLat = lat;
      if (d.lastLocation?.lng > maxLng || !maxLng) maxLng = lng;
      if (d.lastLocation?.lat > maxLat || !maxLat) maxLat = lat;
      d.timestamp = timestamp;
      d.deviceId = d.id;
      d.occupier = Number(isActiveDevice(d));
      features.push({
        type: 'Feature',
        properties: d,
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        }
      });
    }
  });

  if (!features.length) {
    const [iLng, iLat] = defaultMapCenter;
    minLng = iLng;
    maxLng = iLng;
    minLat = iLat;
    maxLat = iLat;
  }

  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features
    },
    cluster: true,
    clusterMaxZoom: 18,
    clusterRadius: 20,
    minLng,
    maxLng,
    minLat,
    maxLat
  };
}

export default {
  addLayer(map) {
    this.map = map;
    const { theme } = map.app;

    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'locations',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'case',
          ['==', ['get', 'occupier'], 0],
          '#8d8d8d',
          '#5eb676'
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          10,
          10,
          18,
          100,
          26,
          750,
          30
        ]
      }
    });

    map.addLayer({
      id: 'places',
      // type: 'symbol',
      type: 'circle',
      source: 'locations',
      filter: ['!=', 'cluster', true],
      paint: {
        // 'circle-color': ['concat', '#', ['get', 'color']],
        'circle-color': [
          'case',
          ['==', ['get', 'occupier'], 0],
          '#8d8d8d',
          '#5eb676'
        ],
        'circle-radius': 5,
        'circle-stroke-width': 2,
        'circle-stroke-color': [
          'case',
          ['==', ['get', 'occupier'], 0],
          '#717171',
          '#5eb676'
        ]
      }
      /* layout: {
        'icon-image': ['get', 'type']
      },
      paint: {
        'icon-color': theme.palette.secondary.main,
        'icon-halo-color': theme.palette.secondary.main,
        'icon-halo-width': 2
      } */
    });

    map.addLayer({
      id: 'checkpoints-text',
      source: 'locations',
      type: 'symbol',
      filter: ['has', 'point_count'],
      paint: {
        'text-color': theme.palette.getContrastText(getContrastColor()),
        'text-opacity': 0.8
      },
      layout: {
        'text-field': '{point_count}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': [
          'step',
          ['get', 'point_count'],
          10,
          10,
          12,
          100,
          14,
          750,
          16
        ],
        'text-padding': 0
      }
    });
  },
  getGeoJSON,
  addClusterProperties,
  addPlaceHover,
  addPlaceClick,
  addClusterHover,
  addClusterClick
};
